import React, { useMemo, useRef } from "react";
import ReactQuill, { Quill } from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const AlignStyle = Quill.import("attributors/style/align");
const ColorStyle = Quill.import("attributors/style/color");
const BackgroundStyle = Quill.import("attributors/style/background");
const FontStyle = Quill.import("attributors/style/font");
const SizeStyle = Quill.import("attributors/style/size");
Quill.register(AlignStyle, true);
Quill.register(ColorStyle, true);
Quill.register(BackgroundStyle, true);
Quill.register(FontStyle, true);
Quill.register(SizeStyle, true);

export const RichTextEditor = ({ value, onChange, placeholder }) => {
  const quillRef = useRef(null);

  React.useEffect(() => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;
    editor.clipboard.addMatcher(Node.TEXT_NODE, (node, delta) => {
      delta.ops.forEach((op) => {
        if (typeof op.insert === "string") op.insert = op.insert.replace(/\u00a0/g, " ");
      });
      return delta;
    });
  }, []);

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [2, 3, false] }],
          [{ font: [] }, { size: ["small", false, "large", "huge"] }],
          ["bold", "italic", "underline", "strike"],
          [{ color: [] }, { background: [] }],
          [{ align: ["", "center", "right", "justify"] }],
          [{ list: "ordered" }, { list: "bullet" }],
          ["blockquote", "link", "image"],
          ["clean"],
        ],
        handlers: {
          image: function () {
            const editor = quillRef.current?.getEditor();
            const input = document.createElement("input");
            input.type = "file";
            input.accept = "image/jpeg,image/png,image/gif,image/webp";
            input.onchange = async () => {
              const file = input.files?.[0];
              if (!file) return;
              if (file.size > 8 * 1024 * 1024) {
                toast.error("Imagem muito grande. Tamanho máximo: 8 MB.");
                return;
              }
              const fd = new FormData();
              fd.append("file", file);
              toast.info("Enviando imagem...");
              try {
                const r = await api.post("/admin/upload", fd, {
                  headers: { "Content-Type": "multipart/form-data" },
                });
                const range = editor?.getSelection(true);
                editor?.insertEmbed(range ? range.index : 0, "image", r.data.url);
                editor?.setSelection((range ? range.index : 0) + 1);
                toast.success("Imagem inserida!");
              } catch (err) {
                toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erro ao enviar a imagem.");
              }
            };
            input.click();
          },
        },
      },
    }),
    []
  );

  return (
    <div data-testid="rich-text-editor" className="rte-wrapper">
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value}
        onChange={onChange}
        modules={modules}
        placeholder={placeholder}
      />
    </div>
  );
};
